import { useCallback, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { graphqlFetch } from 'utils/graphqlFetch';
import { facilityDisplayName } from 'utils/campusLabels';
import { formatDirection, formatShipmentType } from 'utils/shipmentHelpers';

/**
 * A shipment a trailer could take for a typed reference. A T-number is unique only per
 * facility, so an intra-campus transfer has two of these: the outbound leg at the shipping
 * facility and the inbound leg at the receiving one.
 */
export type ShipmentCandidate = {
  id: string;
  reference_number: string;
  direction: string | null;
  status: string | null;
  shipment_type: string | null;
  facility_id: string;
  facility_name: string | null;
  facility_alias: string | null;
  on_this_trailer: boolean;
};

export type ShipmentChoice =
  | { status: 'none' }
  | { status: 'cancelled' }
  | { status: 'chosen'; shipment: ShipmentCandidate };

type ChooseOptions = {
  /** Keep the shipment the trailer already carries instead of asking (for prefilled, read-only references). */
  preferCurrent?: boolean;
};

const CANDIDATES_QUERY = `
  query ShipmentsInCampusByReferenceNumber($referenceNumber: String!, $facilityId: String!, $trailerUuid: String!) {
    shipments_in_campus_by_reference_number(reference_number: $referenceNumber, facility_id: $facilityId, trailer_uuid: $trailerUuid) {
      id
      reference_number
      direction
      status
      shipment_type
      facility_id
      facility_name
      facility_alias
      on_this_trailer
    }
  }
`;

/** Every campus shipment the trailer could take for `reference` (the given facility's first). Throws on a GraphQL error. */
export async function fetchShipmentCandidates(
  reference: string,
  facilityId: string | number,
  trailerUuid: string | null | undefined
): Promise<ShipmentCandidate[]> {
  const result = await graphqlFetch(CANDIDATES_QUERY, {
    referenceNumber: reference.trim(),
    facilityId: String(facilityId),
    trailerUuid: trailerUuid || ''
  });

  if (result?.errors?.length) {
    throw new Error(result.errors[0]?.message || 'Failed to look up shipment');
  }

  return result?.data?.shipments_in_campus_by_reference_number || [];
}

const candidateFacility = (candidate: ShipmentCandidate) =>
  facilityDisplayName({ name: candidate.facility_name || '', facility_alias: candidate.facility_alias }) ||
  `Facility ${candidate.facility_id}`;

/**
 * Resolve a typed shipment reference to one shipment, asking the user which one they mean
 * when it matches more than one in the campus. Render `shipmentChoiceDialog` once in the
 * component that calls `chooseShipment`.
 */
export function useShipmentChoice() {
  const [pending, setPending] = useState<{ reference: string; candidates: ShipmentCandidate[] } | null>(null);
  const resolverRef = useRef<((choice: ShipmentChoice) => void) | null>(null);

  const settle = useCallback((choice: ShipmentChoice) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    resolve?.(choice);
  }, []);

  const chooseShipment = useCallback(
    async (
      reference: string,
      facilityId: string | number,
      trailerUuid: string | null | undefined,
      options: ChooseOptions = {}
    ): Promise<ShipmentChoice> => {
      const candidates = await fetchShipmentCandidates(reference, facilityId, trailerUuid);

      if (candidates.length === 0) return { status: 'none' };
      if (candidates.length === 1) return { status: 'chosen', shipment: candidates[0] };

      const current = options.preferCurrent ? candidates.find(c => c.on_this_trailer) : undefined;
      if (current) return { status: 'chosen', shipment: current };

      // A second lookup while a prompt is open cancels the first.
      resolverRef.current?.({ status: 'cancelled' });

      return new Promise<ShipmentChoice>(resolve => {
        resolverRef.current = resolve;
        setPending({ reference: reference.trim().toUpperCase(), candidates });
      });
    },
    []
  );

  const shipmentChoiceDialog = (
    <Dialog open={!!pending} onClose={() => settle({ status: 'cancelled' })} maxWidth="xs" fullWidth>
      <DialogTitle>Which shipment is {pending?.reference}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" className="mb-3">
          {pending?.reference} exists at more than one facility on this campus. Choose the one this trailer carries.
        </Typography>
        <div className="flex flex-col gap-2">
          {pending?.candidates.map(candidate => (
            <Button
              key={candidate.id}
              variant="outlined"
              onClick={() => settle({ status: 'chosen', shipment: candidate })}
              sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }}
            >
              <div className="flex flex-col">
                <span className="font-semibold">
                  {formatDirection(candidate.direction) || 'Unknown direction'} · {candidateFacility(candidate)}
                </span>
                <span className="text-xs text-gray-600">
                  {[formatShipmentType(candidate.shipment_type), candidate.status, candidate.on_this_trailer ? 'on this trailer now' : '']
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            </Button>
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => settle({ status: 'cancelled' })}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  return { chooseShipment, shipmentChoiceDialog };
}
